import Axios from 'api/client';
import { QuickBooksCustomerLookup } from 'assets/js/pages/account-settings/account-integrations/QuickBooks/constants';
import {
    QuickBooksAccount,
    QuickBooksClass,
    QuickBooksRule,
    QuickBooksServiceItem,
    QuickBooksSettings,
    TransactionMethod,
} from 'types/domain';

interface QuickBooksSetupResponse {
    auth_url: string | null;
}

interface QuickBooksSettingsRequest {
    settings: QuickBooksSettings;
}

interface QuickBooksMappingData {
    accounts?: QuickBooksAccount[];
    item_service?: QuickBooksServiceItem[];
    classes?: QuickBooksClass[];
}

enum QuickBooksProcessMode {
    Updating = 'updating',
    Syncing = 'syncing',
    Maintenance = 'maintenance',
    Rebuild = 'rebuild',
    Idle = 'idle',
    Queued = 'queued',
    Disconnected = 'disconnected',
}

interface QuickBooksDetailsResponse {
    mapping: {
        data: QuickBooksMappingData;
    };
    settings: {
        customer_lookup?: QuickBooksCustomerLookup;
        sync_refunds?: boolean;
        auto_sync?: boolean;
        last_sync?: string;
        next_sync?: string;
        default_donation_mapping?: {
            default_account: number | null;
            default_class: string | null;
            default_service_item: number | null;
            do_not_sync: boolean;
        };
    };
    process_mode: QuickBooksProcessMode;
}

interface QuickBooksRuleOrderResponse {
    order: number[];
}

interface QuickBooksStatusResponse {
    status: 'connected' | 'active' | 'disconnected' | null;
}

interface QuickBooksRuleRequest {
    name: string;
    fund_id: number | null;
    campaign_id: number | null;
    payment_method: TransactionMethod | null;
    do_not_sync: boolean;
    mapping: {
        service_item: string | null;
        class: string | null;
        account: string | null;
    };
}

interface QuickBooksRuleOrderRequest {
    order: number[];
}

const fetchQuickBooksSetup = (accountId: number) =>
    Axios.get<QuickBooksSetupResponse>(`/accounts/${accountId}/quickbooks/setup`).then(
        response => response.data,
    );

const fetchQuickBooksDetails = (accountId: number) =>
    Axios.get<QuickBooksDetailsResponse>(`/accounts/${accountId}/quickbooks/details`).then(
        response => response.data,
    );

const fetchQuickBooksStatus = (accountId: number) =>
    Axios.get<QuickBooksStatusResponse>(`/accounts/${accountId}/quickbooks/status`).then(
        response => response.data,
    );

const saveQuickBooksSetting = (accountId: number, params: QuickBooksSettingsRequest) =>
    Axios.post<QuickBooksDetailsResponse>(
        `/accounts/${accountId}/quickbooks/settings`,
        params,
    ).then(response => response.data);

const fetchQuickBooksRules = (accountId: number) =>
    Axios.get<QuickBooksRule[]>(`/accounts/${accountId}/quickbooks/rules`).then(
        response => response.data,
    );

const createQuickBooksRule = (accountId: number, params: QuickBooksRuleRequest) =>
    Axios.post<QuickBooksRule>(`/accounts/${accountId}/quickbooks/rules`, params).then(
        response => response.data,
    );

const updateQuickBooksRule = (accountId: number, ruleId: number, params: QuickBooksRuleRequest) =>
    Axios.patch<QuickBooksRule>(`/accounts/${accountId}/quickbooks/rules/${ruleId}`, params).then(
        response => response.data,
    );

const updateQuickBooksRuleOrder = (accountId: number, params: QuickBooksRuleOrderRequest) =>
    Axios.patch<QuickBooksRuleOrderResponse>(
        `/accounts/${accountId}/quickbooks/rules/reorder`,
        params,
    ).then(response => response.data);

const deleteQuickBooksRule = (accountId: number, ruleId: number) =>
    Axios.delete(`/accounts/${accountId}/quickbooks/rules/${ruleId}`).then(
        response => response.data,
    );

const fetchQuickBooksRuleById = (accountId: number, ruleId: number) =>
    Axios.get<QuickBooksRule>(`/accounts/${accountId}/quickbooks/rules/${ruleId}`).then(
        response => response.data,
    );

const deleteQuickBooksConnection = (accountId: number) =>
    Axios.delete(`/accounts/${accountId}/quickbooks`).then(response => response.data);

const refreshQuickBooksMapping = (accountId: number) =>
    Axios.post(`/accounts/${accountId}/quickbooks/refreshMapping`).then(response => response.data);

const manualSync = (accountId: number) =>
    Axios.post(`/accounts/${accountId}/quickbooks/batch`).then(response => response.data);

const rebuild = (accountId: number) =>
    Axios.post(`/accounts/${accountId}/quickbooks/rebuild`).then(response => response.data);

export {
    fetchQuickBooksSetup,
    fetchQuickBooksDetails,
    fetchQuickBooksStatus,
    saveQuickBooksSetting,
    createQuickBooksRule,
    updateQuickBooksRule,
    updateQuickBooksRuleOrder,
    deleteQuickBooksRule,
    fetchQuickBooksRuleById,
    fetchQuickBooksRules,
    deleteQuickBooksConnection,
    refreshQuickBooksMapping,
    manualSync,
    rebuild,
    QuickBooksProcessMode,
};

export type {
    QuickBooksSetupResponse,
    QuickBooksDetailsResponse,
    QuickBooksStatusResponse,
    QuickBooksRuleRequest,
    QuickBooksRule,
};
